/**
 * ReceiptCard — branded fee receipt visual block.
 * Shared between FeeCollectionDialog (live collection) and ReceiptViewDialog
 * (re-opening a historic receipt from /payments).
 */
import React from "react";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function monthLabel(m) {
  if (!m || m.length < 7) return null;
  const y = parseInt(m.slice(0, 4), 10);
  const mm = parseInt(m.slice(5, 7), 10);
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!y || mm < 1 || mm > 12) return m;
  return `${names[mm - 1]} ${y}`;
}

export default function ReceiptCard({ school, payment }) {
  const periodLabel = monthLabel(payment.period_month);
  return (
    <div
      className="border border-slate-200 rounded-md p-5 bg-white print:border-black"
      data-testid="receipt-preview-card"
    >
      <div className="flex items-start justify-between gap-3 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div
            className="h-12 w-12 rounded flex items-center justify-center text-white font-bold font-heading"
            style={{ backgroundColor: school?.theme_color || "#1E3A8A" }}
          >
            {(school?.school_name || "S").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-slate-900 font-heading text-base">{school?.school_name}</div>
            <div className="text-[11px] text-slate-500">{school?.address}</div>
            <div className="text-[11px] text-slate-500">
              {school?.phone} {school?.email ? ` · ${school.email}` : ""}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Receipt</div>
          <div className="font-mono text-base text-slate-900">{payment.receipt_no}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {new Date(payment.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 py-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Student</div>
          <div className="text-slate-900 font-medium">{payment.student_name}</div>
          <div className="text-xs text-slate-500 font-mono">{payment.student_admission_no}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Class · Session</div>
          <div className="text-slate-900">{payment.class_name}</div>
          <div className="text-xs text-slate-500">{payment.session_name}</div>
        </div>
      </div>

      <table className="w-full text-sm border-t border-slate-200">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="py-2">Particulars</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100">
            <td className="py-2 text-slate-900">
              {payment.head_snapshot}
              {periodLabel && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-semibold uppercase tracking-wider" data-testid="receipt-period-month">
                  For {periodLabel}
                </span>
              )}
            </td>
            <td className="py-2 text-right font-mono text-slate-900">{formatINR(payment.amount_paid)}</td>
          </tr>
          <tr className="border-t border-slate-100 bg-slate-50">
            <td className="py-2 font-semibold">Total received</td>
            <td className="py-2 text-right font-mono font-bold">{formatINR(payment.amount_paid)}</td>
          </tr>
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Mode</div>
          <div className="text-slate-900 capitalize">{payment.payment_mode}</div>
        </div>
        {payment.txn_ref && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Txn ref</div>
            <div className="text-slate-900 font-mono">{payment.txn_ref}</div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Collected by</div>
          <div className="text-slate-900">{payment.collected_by_name}</div>
        </div>
        {school?.upi_id && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">UPI</div>
            <div className="text-slate-900 font-mono">{school.upi_id}</div>
          </div>
        )}
      </div>

      {school?.receipt_footer && (
        <div className="mt-4 pt-3 border-t border-slate-200 text-center text-xs text-slate-500 italic">
          {school.receipt_footer}
        </div>
      )}
    </div>
  );
}
